import { HttpEventType } from '@angular/common/http';
import { Component, computed, DestroyRef, inject, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { faClose, faFile, faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';

import { catchError, defaultIfEmpty, filter, finalize, from, map, mergeMap, Observable, of, take, tap, toArray } from 'rxjs';

import { MediaService } from '@eiq/files';
import { Media } from '@eiq/files/models';

import { FileType, FileTypeExtension, FileTypeTexts, ImageTypes } from '@empathy/global/data';

import { StimulusPickerSettings } from '../../../models/stimulus/stimulus-picker-settings.interface';
import { StimulusUploadEntry } from '../../../models/stimulus/stimulus-upload-entry.interface';

@Component({
  selector: 'empathy-stimulus-picker-dialog',
  templateUrl: 'stimulus-picker-dialog.component.html',
  styleUrls: ['./stimulus-picker-dialog.component.scss']
})
export class StimulusPickerDialogComponent implements OnInit, OnDestroy {
  readonly uploadCandidates$ = computed(() => this.entries$().filter((entry) => entry.state === 'ready' || entry.state === 'failed'));
  readonly uploadedFiles$ = computed(() => this.entries$().flatMap((entry) => (entry.media ? [entry.media] : [])));
  readonly maxFilesExceeded$ = computed(() => !this.data.multipleInd && this.entries$().length > 1);
  readonly saveDisabled$ = computed(() => this.uploading$() || this.maxFilesExceeded$() || (!this.uploadCandidates$().length && !this.uploadedFiles$().length));
  readonly saveText$ = computed(() => {
    if (!this.uploadCandidates$().length) {
      return 'Use uploaded';
    }
    if (this.entries$().some((entry) => entry.state === 'failed')) {
      return 'Retry upload';
    }
    return 'Save';
  });
  readonly summary$ = computed(() => {
    if (!this.batchAttempted$() || this.uploading$()) {
      return '';
    }
    const failed = this.entries$().filter((entry) => entry.state === 'failed').length;
    const skipped = this.entries$().filter((entry) => entry.state === 'invalid').length;
    return `${this.uploadedFiles$().length} uploaded; ${failed} failed; ${skipped} skipped. Successful uploads are kept when you close.`;
  });

  readonly entries$ = signal<StimulusUploadEntry[]>([]);
  readonly fileTypeTexts$ = signal<FileTypeExtension[]>([]);
  readonly uploading$ = signal(false);
  readonly batchAttempted$ = signal(false);
  readonly batchSize$ = signal(0);
  readonly batchCompleted$ = signal(0);

  readonly faClose = faClose;
  readonly faFile = faFile;
  readonly faPlus = faPlus;
  readonly faTrash = faTrash;
  readonly fileType = FileType;
  readonly maxSize = 50; // MB

  private readonly mediaService = inject(MediaService);
  private readonly destroyRef = inject(DestroyRef);
  private nextEntryId = 0;

  constructor(
    private dialogRef: MatDialogRef<StimulusPickerDialogComponent, Media[]>,
    @Inject(MAT_DIALOG_DATA) public data: StimulusPickerSettings
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('560px');
    this.dialogRef.disableClose = true;
    this.fileTypeTexts$.set(this.data.types.map((type) => FileTypeTexts[type]));
  }

  ngOnDestroy(): void {
    for (const entry of this.entries$()) {
      if (entry.src) {
        URL.revokeObjectURL(entry.src);
      }
    }
  }

  onSave(): void {
    if (this.saveDisabled$()) {
      return;
    }
    const candidates = this.uploadCandidates$();
    if (!candidates.length) {
      this.onCancel();
      return;
    }
    this.uploading$.set(true);
    this.batchAttempted$.set(true);
    this.batchSize$.set(candidates.length);
    this.batchCompleted$.set(0);
    // Limit active requests; entry identities preserve selection order regardless of response order.
    from(candidates)
      .pipe(
        mergeMap((entry) => this.uploadFile(entry), 3),
        toArray(),
        finalize(() => this.uploading$.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (this.entries$().every((entry) => entry.state === 'uploaded')) {
          this.dialogRef.close(this.uploadedFiles$());
        }
      });
  }

  onCancel(): void {
    if (this.uploading$()) {
      return;
    }
    const media = this.uploadedFiles$();
    this.dialogRef.close(media.length ? media : undefined);
  }

  onUploadMore(event: Event): void {
    const input = event.target as HTMLInputElement;
    for (const file of Array.from(input.files ?? [])) {
      this.onUpload(file);
    }
    input.value = '';
  }

  onUpload(file: File): void {
    if (this.uploading$()) {
      return;
    }
    let message: string;
    if (file.size > this.maxSize * 1024 * 1024) {
      message = `The file is too big (max ${this.maxSize} MB).`;
    } else if (!this.data.types.some((type) => type === file.type)) {
      message = 'This file type is not supported.';
    }
    let src: string;
    if (!message) {
      try {
        src = URL.createObjectURL(file);
      } catch {
        message = 'Could not preview this file. Remove it and select it again.';
      }
    }
    if (!this.entries$().length) {
      this.handleDialogSize(true);
    }
    this.entries$.update((entries) => [
      ...entries,
      {
        id: ++this.nextEntryId,
        file,
        size: (file.size / (1024 * 1024)).toFixed(2),
        src,
        isImage: (ImageTypes as string[]).includes(file.type),
        state: message ? 'invalid' : 'ready',
        message
      }
    ]);
  }

  onDelete(entryId: number): void {
    if (this.uploading$()) {
      return;
    }
    const entry = this.entries$().find((item) => item.id === entryId);
    if (entry?.state === 'uploaded') {
      return;
    }
    if (entry?.src) {
      URL.revokeObjectURL(entry.src);
    }
    this.entries$.update((entries) => entries.filter((item) => item.id !== entryId));
    if (!this.entries$().length) {
      this.handleDialogSize(false);
    }
  }

  private uploadFile(entry: StimulusUploadEntry): Observable<Media | null> {
    const fileData = new FormData();
    fileData.append('mediaFile', entry.file);
    fileData.append('itemReference', entry.file.name);
    this.updateEntry(entry.id, { state: 'uploading', message: undefined });
    return this.mediaService.uploadStimulus(fileData).pipe(
      filter((event) => !event || event.type === HttpEventType.Response),
      map((event): Media | null => {
        if (!event || event.type !== HttpEventType.Response || typeof event.body?.id !== 'string' || !event.body.id.trim()) {
          return null;
        }
        return { mediaId: event.body.id, name: entry.file.name, type: entry.file.type } as Media;
      }),
      defaultIfEmpty(null),
      catchError(() => of(null)),
      take(1),
      tap((media) => {
        this.updateEntry(entry.id, { state: media ? 'uploaded' : 'failed', media, message: media ? 'Uploaded' : 'Upload failed. Retry or remove this file.' });
        this.batchCompleted$.update((count) => count + 1);
      })
    );
  }

  private updateEntry(id: number, changes: Partial<StimulusUploadEntry>): void {
    this.entries$.update((entries) => entries.map((entry) => (entry.id === id ? { ...entry, ...changes } : entry)));
  }

  private handleDialogSize(increase: boolean): void {
    if (!increase) {
      this.dialogRef.updateSize('560px');
    } else if (this.data.multipleInd) {
      this.dialogRef.updateSize('1024px', '778px');
    } else {
      this.dialogRef.updateSize('560px', '587px');
    }
  }
}
